import React, { useEffect, useRef, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import {
  formatPercentForInput,
  isPercentInputIntermediate,
  parsePercent,
} from '../utils/parsers';
import { validatePercent } from '../utils/validators';

export interface PercentFieldProps {
  label: string;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  allowZero?: boolean;
  min?: number;
  max?: number;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  tooltip?: string;
}

export function PercentField({
  label,
  value,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  allowZero = false,
  error: errorProp,
  helperText: helperTextProp,
  size = 'medium',
  fullWidth = true,
  tooltip,
}: PercentFieldProps): React.ReactElement {
  const [inputStr, setInputStr] = useState<string>(
    () => formatPercentForInput(value)
  );
  const touched = useRef(false);
  const [, forceRender] = useState(0);

  useEffect(() => {
    setInputStr(formatPercentForInput(value));
  }, [value]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const raw = e.target.value;
    if (!isPercentInputIntermediate(raw)) return;
    setInputStr(raw);
    onChange(parsePercent(raw));
  }

  function handleBlur(): void {
    touched.current = true;
    forceRender((n) => n + 1);
    onBlur?.();
  }

  function handleWheel(e: React.WheelEvent<HTMLDivElement>): void {
    (e.target as HTMLElement).blur();
  }

  const parsedValue = parsePercent(inputStr);

  let computedError = false;
  let computedHelperText = '';

  if (errorProp !== undefined) {
    computedError = errorProp;
    computedHelperText = helperTextProp ?? '';
  } else if (touched.current) {
    const msg = validatePercent(parsedValue, {
      required,
      allowZero,
      fieldLabel: label,
    });
    computedError = msg !== '';
    computedHelperText = msg;
  }

  const field = (
    <TextField
      label={label}
      value={inputStr}
      onChange={handleChange}
      onBlur={handleBlur}
      onWheel={handleWheel}
      disabled={disabled}
      required={required}
      size={size}
      fullWidth={fullWidth}
      error={computedError}
      helperText={computedHelperText}
      inputProps={{ inputMode: 'decimal' }}
      type="text"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">%</InputAdornment>
        ),
      }}
    />
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{field}</Tooltip>;
  }

  return field;
}